import { useContext, useRef } from "react"
import { Button, Flex, Form, Input, Modal, Select } from "antd"
import { useCostCodes } from "../../../../domain/costCodes/costCodeQueries"
import { useDivisions } from "../../../../domain/divisions/divisionsQueries"
import { useCreateWorkareaMutation } from "../../../../domain/workareas/workareaQueries"
import { CreateWorkareaRequest } from "../../../../domain/workareas/workareaApi"
import { ItemsContext } from "../../../Items/ItemsRoot"

export type CreateWorkareaForm = {
  name: string
  costCodeId: number
  divisionId: number
  jobId: number
}

type RepeatsEvery = {
  qty: number
  type: string
}

type RepeatsAt = {
  startTime: string
  endTime: string
}

export type RecurringScheduleConfig = {
  startsOn: string
  repeatsEvery: RepeatsEvery
  repeatsAt: RepeatsAt
  endsOn: string
}

type Props = {
  isOpen: boolean
  onCancel: () => void
  jobId: number
  groupId?: string
  onWorkareaCreated?: (newWorkareaId: number) => void
}

const CreateWorkareaModal = ({ isOpen, onCancel, jobId, groupId, onWorkareaCreated }: Props) => {
  const { jobData: { jobType } } = useContext(ItemsContext)

  const [form] = Form.useForm<CreateWorkareaForm>()
  const autoFocusRef = useRef<any>()

  const costCodesQuery = useCostCodes()
  const costCodeOptions = costCodesQuery.data?.map(x => ({ label: x.name, value: x.id })) ?? []

  const divisionsQuery = useDivisions()
  const divisionOptions = divisionsQuery.data
    ?.filter(x => x.type === jobType)
    ?.map(x => ({ label: x.name, value: x.id })) ?? []

  const createWorkareaMutation = useCreateWorkareaMutation((newWorkareaId) => {
    onWorkareaCreated?.(newWorkareaId)
    handleCancel()
  })

  const handleCreateWorkarea = () => {
    const formValues = form.getFieldsValue()

    const request: CreateWorkareaRequest = {
      name: formValues.name,
      jobId: jobId,
      costCodeId: formValues.costCodeId,
      divisionId: formValues.divisionId,
      groupId
    }

    createWorkareaMutation.mutate(request)
  }

  const handleCancel = () => {
    onCancel()
    resetForm()
  }

  const resetForm = () => {
    form.setFieldsValue({
      jobId: 0,
      name: '',
      costCodeId: undefined,
      divisionId: undefined,
    })
  }

  const handleOpen = () => {
    if (autoFocusRef.current) {
      autoFocusRef.current.focus()
    }
  }

  return <Modal
    open={isOpen}
    onCancel={handleCancel}
    title="Create Work Area"
    forceRender
    footer={false}
    width={600}
    afterOpenChange={handleOpen}
  >
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 6 }}
      wrapperCol={{ span: 18 }}
      style={{ marginTop: 24 }}
      onFinish={handleCreateWorkarea}>
      <Form.Item
        name="name"
        label="Work Area Name"
        rules={[
          {
            required: true,
            message: 'Please input a Work Area Name',
          },
        ]}>
        <Input ref={autoFocusRef} maxLength={75} />
      </Form.Item>
      <Form.Item
        name="costCodeId"
        label="Cost Code"
        rules={[
          {
            required: true,
            message: 'Please input a Cost Code',
          },
        ]}>
        <Select
          options={costCodeOptions}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item
        name="divisionId"
        label="Division"
        rules={[
          {
            required: true,
            message: 'Please input a Division',
          },
        ]}>
        <Select
          options={divisionOptions}
          showSearch
          optionFilterProp="label"
        />
      </Form.Item>
      <Flex style={{ justifyContent: 'flex-end' }}>
        <Button onClick={handleCancel} style={{ marginRight: '10px' }} disabled={createWorkareaMutation.isLoading}>
          Cancel
        </Button>
        <Button type="primary" htmlType="submit" disabled={createWorkareaMutation.isLoading} loading={createWorkareaMutation.isLoading}>
          Create Work Area
        </Button>
      </Flex>
    </Form>
  </Modal>
}

export default CreateWorkareaModal
